import { push } from 'connected-react-router';
import CoreAPIService from '../../../utils/core-api';
import ObjectUtils from '../../../utils/objectUtils';
import { fetchAction } from '../../actions';
import envConfig from '../../../config';
import {
    PartnerById,
    DriverArrayById,
    VehicleArrayById,
    ManagerArrayById,
    PartnerBillArrayById,
    LightStructure,
    VehicleType,
    Rule,
} from '../api-types';
import { updateDocument } from '../shared/document-utils';
import { types as documentTypes } from '../documents/actionTypes';
import { createSelector } from 'reselect';
import { createPath, paths } from '../../../routes/paths';
import { name, types } from './actionTypes';

interface PartnerState {
    partners: PartnerById;
    partner_drivers: DriverArrayById;
    partner_vehicles: VehicleArrayById;
    partner_managers: ManagerArrayById;
    partner_bills_log: PartnerBillArrayById;
    vehicles_models: VehicleType[];
    vehicles_colors: LightStructure[];
    vehicles_motorizations: LightStructure[];
    payment_rules: Rule[];
}

const initialState: PartnerState = {
    partners: {} as PartnerById,
    partner_drivers: {} as DriverArrayById,
    partner_vehicles: {} as VehicleArrayById,
    partner_managers: {} as ManagerArrayById,
    partner_bills_log: {} as PartnerBillArrayById,
    vehicles_models: [],
    vehicles_colors: [],
    vehicles_motorizations: [],
    payment_rules: [],
};

const reducer = (state = initialState, action): PartnerState => {
    const payload = action.payload;
    const parameters = action.parameters;
    const id = parameters ? parameters.id : 0;
    const idm = parameters ? parameters.idm : 0;
    switch (action.type) {
        case types.GET_PARTNER:
        case types.EDIT_PARTNER:
            let processedDocuments = payload.documents;
            if (payload.documents?.contract?.current_version?.user_id) {
                const contractUserId = payload.documents.contract.current_version.user_id;
                processedDocuments = {
                    ...ObjectUtils.keepIds(payload.documents),
                    current_version: {
                        user_id: contractUserId,
                    },
                };
            } else {
                processedDocuments = ObjectUtils.keepIds(payload.documents);
            }

            action = {
                ...action,
                payload: {
                    ...action.payload,
                    documents: processedDocuments,
                },
            };
            return {
                ...state,
                partners: {
                    ...state.partners,
                    [id]: state.partners[id] ? { ...state.partners[id], ...action.payload } : action.payload,
                },
            };
        case types.SAVE_PARTNER_DRIVER:
            return {
                ...state,
                partner_drivers: {
                    ...state.partner_drivers,
                    [id]: state.partner_drivers[id] ? [...state.partner_drivers[id], action.payload] : [action.payload],
                },
            };
        case types.GET_PARTNER_MANAGER:
        case types.SAVE_PARTNER_MANAGER:
            return {
                ...state,
                partner_managers: {
                    ...state.partner_managers,
                    [id]: state.partner_managers[id]
                        ? [...state.partner_managers[id], action.payload]
                        : [action.payload],
                },
            };
        case types.EDIT_PARTNER_MANAGER:
            const managers = (state.partner_managers[id] ?? []).filter((m) => String(m.id) !== String(idm));
            return {
                ...state,
                partner_managers: {
                    ...state.partner_managers,
                    [id]: [...managers, action.payload],
                },
            };
        case types.SAVE_PARTNER_VEHICLE:
            return {
                ...state,
                partner_vehicles: {
                    ...state.partner_vehicles,
                    [id]: state.partner_vehicles[id]
                        ? [...state.partner_vehicles[id], action.payload]
                        : [action.payload],
                },
            };
        case types.GET_PARTNER_DRIVERS:
            action = { ...action, payload: { data: action.payload } };
            return {
                ...state,
                partner_drivers: { ...state.partner_drivers, [id]: action.payload.data },
            };
        case types.GET_PARTNER_VEHICLES:
            action = { ...action, payload: { data: action.payload } };
            return {
                ...state,
                partner_vehicles: { ...state.partner_vehicles, [id]: action.payload.data },
            };
        case types.GET_PARTNER_MANAGERS:
            action = { ...action, payload: { data: action.payload } };
            return {
                ...state,
                partner_managers: { ...state.partner_managers, [id]: action.payload.data },
            };
        case types.GET_PARTNER_BILLS_LOG:
            action = { ...action, payload: { data: action.payload } };
            return {
                ...state,
                partner_bills_log: { ...state.partner_bills_log, [id]: action.payload.data },
            };
        case types.GET_VEHICLE_COLORS:
            return {
                ...state,
                vehicles_colors: action.payload,
            };
        case types.GET_VEHICLE_MODELS:
            return {
                ...state,
                vehicles_models: action.payload,
            };
        case types.GET_VEHICLE_MOTORIZATIONS:
            return {
                ...state,
                vehicles_motorizations: action.payload,
            };
        case types.REGENERATE_PARTNERSHIP_CONTRACT:
            return updateDocument('partner', 'partners', state, payload.data);
        case types.SAVE_PARTNERSHIP_CONTRACT:
        case documentTypes.UPSERT_DOC:
            return updateDocument('partner', 'partners', state, payload);

        case types.GET_PAYMENT_RULES:
            return {
                ...state,
                payment_rules: payload,
            };

        case types.SET_PAYMENT_RULE:
            const newState = ObjectUtils.deepCopy(state);
            newState.partners[id].active_payment_rule_association = payload.active_payment_rule_association;
            newState.partners[id].non_expired_payment_rule_associations = payload.non_expired_payment_rule_associations;

            return newState;

        default:
            return state;
    }
};
export default reducer;

export const actions = {
    getPartner: (id) => fetchAction(CoreAPIService.get(`/core/partners/${id}`), types.GET_PARTNER, { id }),
    getPartnerDrivers: (id) =>
        fetchAction(CoreAPIService.get(`/core/partners/${id}/drivers`), types.GET_PARTNER_DRIVERS, { id }),
    getPartnerVehicles: (id) =>
        fetchAction(CoreAPIService.get(`/core/partners/${id}/vehicles`), types.GET_PARTNER_VEHICLES, { id }),
    getPartnerManagers: (id) =>
        fetchAction(CoreAPIService.get(`/core/partners/${id}/managers`), types.GET_PARTNER_MANAGERS, { id }),
    getPartnerBills: (id) =>
        fetchAction(CoreAPIService.get(`/core/partners/${id}/bills`), types.GET_PARTNER_BILLS_LOG, { id }),
    editPartner: (id, payload) =>
        fetchAction(
            CoreAPIService.put(`/core/partners/${id}`, payload),
            types.EDIT_PARTNER,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.partners}/${id}`));
                },
            },
        ),
    savePartner: (payload) =>
        fetchAction(
            CoreAPIService.post(`/core/partners`, payload),
            types.SAVE_PARTNER,
            {},
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.partners}/`));
                },
            },
        ),
    getPartnerModels: () => fetchAction(CoreAPIService.get(`/core/vehicles/models`), types.GET_VEHICLE_MODELS, {}),
    getPartnerColors: () => fetchAction(CoreAPIService.get(`/core/vehicles/colors`), types.GET_VEHICLE_COLORS, {}),
    getPartnerMotorizations: () =>
        fetchAction(CoreAPIService.get(`/core/vehicles/motorizations`), types.GET_VEHICLE_MOTORIZATIONS, {}),
    saveVehicle: (id, payload) =>
        fetchAction(
            CoreAPIService.post(`/core/vehicles`, payload),
            types.SAVE_PARTNER_VEHICLE,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.partners}/${id}`));
                },
            },
        ),
    saveDriver: (id, payload) =>
        fetchAction(
            CoreAPIService.post(`/core/drivers`, payload),
            types.SAVE_PARTNER_DRIVER,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.partners}/${id}`));
                },
            },
        ),
    getManager: (id, idm) =>
        fetchAction(CoreAPIService.get(`/core/managers/${idm}`), types.GET_PARTNER_MANAGER, { id }),
    editManager: (id, idm, payload) =>
        fetchAction(
            CoreAPIService.put(`/core/managers/${idm}`, payload),
            types.EDIT_PARTNER_MANAGER,
            { id, idm },
            {
                success: (dispatch) => {
                    dispatch(push(createPath(paths.partnersManagersDetails, { id, idm })));
                },
            },
        ),
    saveManager: (id, payload) =>
        fetchAction(
            CoreAPIService.post(`/core/managers`, payload),
            types.SAVE_PARTNER_MANAGER,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.partners}/${id}`));
                },
            },
        ),
    resetManagerPassword: (id, callback) =>
        fetchAction(
            CoreAPIService.put(`/core/managers/${id}/password_reset`, {}),
            types.RESET_MANAGER_PASSWORD,
            { id },
            { success: callback },
        ),
    saveAdjustment: (payload) =>
        fetchAction(CoreAPIService.post(`/billing/adjustments`, payload), types.SAVE_ADJUSTMENT, {}),
    savePartnershipContract: (id, payload) =>
        fetchAction(CoreAPIService.post(`/core/partners/${id}/contract`, payload), types.SAVE_PARTNERSHIP_CONTRACT, {
            id,
            type: payload.type,
        }),
    regeneratePartnershipContract: (idPartner, managerId) =>
        fetchAction(
            CoreAPIService.post(
                `/internal/partner/generate-contract`,
                {
                    partnerId: idPartner.toString(),
                    managerId: managerId.toString(),
                },
                null,
                envConfig.privapi_url,
            ),
            types.REGENERATE_PARTNERSHIP_CONTRACT,
            {},
        ),
    getPaymentRules: (success?) =>
        fetchAction(
            CoreAPIService.get(`/core/payment_rules`),
            types.GET_PAYMENT_RULES,
            {},
            {
                success: (dispatch) => {
                    if (success) {
                        success();
                    }
                },
            },
        ),
    setPartnerPaymentRule: (id, payload) =>
        fetchAction(CoreAPIService.patch(`/core/partners/${id}/payment_rules`, payload), types.SET_PAYMENT_RULE, {
            id,
        }),
};

const localState = (state: any): PartnerState => state[name] || initialState;
export const selectors = {
    partners: (state) => localState(state).partners,
    partnersDrivers: (state) => localState(state).partner_drivers,
    partnersVehicles: (state) => localState(state).partner_vehicles,
    partnersManagers: (state) => localState(state).partner_managers,
    partnersManagersWithPartner: (state, idPartner) => localState(state).partner_managers?.[idPartner] ?? [],
    partnersBillsLog: (state) => localState(state).partner_bills_log,
    vehicleModels: (state, props) => localState(state).vehicles_models,
    vehicleColors: (state) => localState(state).vehicles_colors,
    vehicleMotorizations: (state) => localState(state).vehicles_motorizations,
    paymentRules: (state) => localState(state).payment_rules,
};

const getModelsName = (models) => {
    if (!(models instanceof Array)) {
        return [];
    }
    return models.map(
        ({
            id,
            brand: { name },
            localized_string: { fr, en },
            type: {
                localized_name: { fr: typeFr, en: typeEn },
            },
            max_age,
        }) => {
            return {
                id,
                name: `${name} ${fr} (${typeFr}) ${max_age ? ' - ' + max_age + ' mois max' : ''}`,
            };
        },
    );
};

export const makeMemoizedGetModelsName = () => {
    return createSelector(selectors.vehicleModels, (transactions) => getModelsName(transactions));
};
